import { DisposalSet, select, selectAll } from '../../../utils';
import { reinit as reinitCategoryTreeFacet } from '../../../widgets/aoc-category-tree-facet';
import { reinit as reinitCategoryListFacet } from '../../../widgets/aoc-category-list-facet';
const SPINNER_MIN_DISPLAY_DURATION = 400;
export const toMultiParamMap = params => {
  const result = new Map();
  for (const [key, value] of params.entries()) {
    addToMultiParamMap(result, key, value);
  }
  return result;
};
export const toURLSearchParams = multiParamMap => {
  const params = new URLSearchParams();
  for (const [key, values] of multiParamMap.entries()) {
    for (const value of values) {
      params.append(key, value);
    }
  }
  return params;
};
export const addToMultiParamMap = (multiParamMap, key, value) => {
  if (multiParamMap.has(key)) {
    const existingSet = multiParamMap.get(key);
    if (Array.isArray(value)) {
      for (const v of value) {
        existingSet.add(v);
      }
    } else {
      existingSet.add(value);
    }
  } else {
    multiParamMap.set(key, new Set(Array.isArray(value) ? value : [value]));
  }
  return multiParamMap;
};
export const replaceInMultiParamMap = (multiParamMap, key, value) => {
  const nSet = new Set(Array.isArray(value) ? value : [value]);
  if (nSet.size === 0) {
    multiParamMap.delete(key);
  } else {
    multiParamMap.set(key, nSet);
  }
  return multiParamMap;
};
export const removeFromMultiParamMap = (multiParamMap, key, value = null) => {
  if (multiParamMap.has(key)) {
    if (value === null) {
      multiParamMap.delete(key);
    } else {
      const set = multiParamMap.get(key);
      if (set) {
        if (Array.isArray(value)) {
          for (const v of value) {
            set.delete(v);
          }
        } else {
          set.delete(value);
        }
        if (set.size === 0) {
          multiParamMap.delete(key);
        }
      }
    }
  }
  return multiParamMap;
};
export const selectors = {
  spinner: '.aoc-search-layout__spinner',
  countText: '.aoc-search-layout__count-text',
  resultContainer: '.aoc-search-layout__results',
  facetsContainer: '.aoc-search-layout__facets-container',
  activeFacetsWidget: '.aoc-search-layout__active-facets',
  activeFacetsList: '.aoc-search-layout__active-facets__list',
  queryInput: '.aoc-pub-search-bar__input',
  categoryListFacet: '.aoc-category-list-facet',
  categoryTreeFacet: '.aoc-category-tree-facet',
  activeFacetRemove: '.aoc-search-layout__active-facets__list .aoc-chip__remove',
  activeFacetsRemoveAll: '.aoc-search-layout__active-facets__clear'
};
const activeFacetGC = new DisposalSet();
export const reinitActiveFacets = (searchLayoutEl, queryInputEl, pageParam) => {
  activeFacetGC.disposeAll();
  const clearEls = selectAll(searchLayoutEl, selectors.activeFacetRemove);
  const clearAllEl = searchLayoutEl.querySelector(selectors.activeFacetsRemoveAll);
  if (!clearAllEl) return;
  for (const clearEl of clearEls) {
    activeFacetGC.register(clearEl).addListener('click', e => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const facetKind = clearEl.dataset.facetKind;
      const facetParam = clearEl.dataset.facetParam;
      const facetValue = clearEl.dataset.facetValue;
      if (facetKind === 'query') {
        if (queryInputEl) queryInputEl.value = '';
      }
      if (!facetParam || !facetValue) {
        updateResults(searchLayoutEl, clearAllEl.href, pageParam);
      } else {
        // this path is preferable because it would make it possible to remove a 2nd (or 3rd, 4th, etc)
        // active facets before the request triggered by removing the 1st facet completed. Otherwise, the
        // 1st facet would re-appear after the 2nd facet request completed
        const multiParamMap = toMultiParamMap(new URLSearchParams(window.location.search));
        removeFromMultiParamMap(multiParamMap, facetParam, facetKind === 'query' ? null : facetValue);
        removeFromMultiParamMap(multiParamMap, pageParam, null);
        const params = toURLSearchParams(multiParamMap);
        const nextUrl = `${window.location.pathname}?${params}`;
        updateResults(searchLayoutEl, nextUrl, pageParam);
      }
    });
  }
  activeFacetGC.register(clearAllEl).addListener('click', e => {
    e.preventDefault();
    e.stopPropagation();
    if (queryInputEl) queryInputEl.value = '';
    updateResults(searchLayoutEl, clearAllEl.href, pageParam);
  });
};
export const forceHardPagination = resultsEl => {
  const paginationLinks = selectAll(resultsEl, '.pagination-bar nav a');
  for (const link of paginationLinks) {
    link.dataset.sennaOff = 'true';
  }
};
const httpState = {
  requestsInFlight: 0,
  lastRequestStarted: 0,
  lastRequestComplete: 0
};
export const updateResults = async (searchLayoutEl, nextUrl, pageParam) => {
  const start = Date.now();
  try {
    const resultsWrapperEl = select(searchLayoutEl, selectors.resultContainer);
    const countTextEl = select(searchLayoutEl, selectors.countText);
    const spinnerEl = select(searchLayoutEl, selectors.spinner);
    const activeFacetsWidgetEl = searchLayoutEl.querySelector(selectors.activeFacetsWidget);
    const queryInputEl = select(searchLayoutEl, selectors.queryInput);
    httpState.requestsInFlight++;
    spinnerEl.style.display = 'block';
    window.history.pushState({
      redirectPath: window.location.pathname + window.location.search,
      path: nextUrl,
      referrer: window.location.href,
      senna: true,
      scrollLeft: 0,
      scrollTop: 0,
      form: false
    }, '', nextUrl);
    const myReqOffset = ++httpState.lastRequestStarted;
    const resp = await fetch(nextUrl);
    const html = await resp.text();
    if (myReqOffset >= httpState.lastRequestComplete) {
      httpState.lastRequestComplete = myReqOffset;
      const parser = new DOMParser();
      const resultDoc = parser.parseFromString(html, 'text/html');
      const updateResultsEl = select(resultDoc, selectors.resultContainer);
      const updateCountTextEl = select(resultDoc, selectors.countText);
      const updateActiveFacetsWidgetEl = resultDoc.querySelector(selectors.activeFacetsWidget);
      const updateActiveFacetsListEl = resultDoc.querySelector(selectors.activeFacetsList);
      const updateFacetControlsEl = resultDoc.querySelector(selectors.facetsContainer);
      const updateCategoryListFacetEls = updateFacetControlsEl ? selectAll(updateFacetControlsEl, selectors.categoryListFacet) : [];
      const updateCategoryTreeFacetEls = updateFacetControlsEl ? selectAll(updateFacetControlsEl, selectors.categoryTreeFacet) : [];
      const doUpdate = () => {
        const facetControlsEl = searchLayoutEl.querySelector(selectors.facetsContainer);
        resultsWrapperEl.innerHTML = updateResultsEl.innerHTML;
        countTextEl.innerText = updateCountTextEl.innerText.trim();
        if (activeFacetsWidgetEl && updateActiveFacetsWidgetEl) {
          activeFacetsWidgetEl.innerHTML = updateActiveFacetsWidgetEl.innerHTML;
          if (updateActiveFacetsListEl && updateActiveFacetsListEl.children.length === 0) {
            activeFacetsWidgetEl.style.display = 'none';
          } else {
            activeFacetsWidgetEl.style.display = 'flex';
          }
        }
        if (facetControlsEl) {
          reinitCategoryListFacet(facetControlsEl, updateCategoryListFacetEls, pageParam);
          reinitCategoryTreeFacet(facetControlsEl, updateCategoryTreeFacetEls, pageParam);
        }
        reinitActiveFacets(searchLayoutEl, queryInputEl, pageParam);
        forceHardPagination(resultsWrapperEl);
        httpState.requestsInFlight--;
        if (httpState.requestsInFlight === 0) {
          spinnerEl.style.display = 'none';
        }
      };
      const delta = Date.now() - start;
      if (delta < SPINNER_MIN_DISPLAY_DURATION) {
        setTimeout(doUpdate, SPINNER_MIN_DISPLAY_DURATION - delta);
      } else {
        doUpdate();
      }
    } else {
      // console.log("a newer request has already been completed, ignoring this one");
      httpState.requestsInFlight--;
    }
  } catch (error) {
    console.error("Something went wrong: " + error);
    httpState.requestsInFlight--;
    window.location.href = nextUrl;
  }
};
if (import.meta.webpackHot) {
  import.meta.webpackHot.accept();
  import.meta.webpackHot.dispose(activeFacetGC.disposeAll);
}